import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { isNil } from 'ramda';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { isLoginAsSelector } from 'store/auth/selectors';
import { isAgentSelector } from 'store/user/selectors';
import { isDefined } from 'utils/ramda';

import { CommonModalData } from '../../../types';
import * as types from '../../types';
import { PropertyFormValues } from '../../types';
import { propertyFormSchema } from './validation';

type Props = {
  modalData?: CommonModalData;
};

function usePropertyForm({ modalData }: Props) {
  const initialValues = modalData?.initialValues;
  const page = modalData?.data.details;

  const name = React.useMemo(() => {
    if (!initialValues) return '';
    if ('attachedPages' in initialValues) return initialValues?.attachedPages?.[0]?.name || '';
    if ('attachedPage' in initialValues) {
      return initialValues?.attachedPage?.name || '';
    }
    if (initialValues.name) return initialValues.name;

    return '';
  }, [initialValues]);

  const defaultValues: PropertyFormValues = page
    ? {
        address: '',
        name,
        ...page,
        masterProperty:
          !page.masterPropertyId && !page.masterPropertyName
            ? undefined
            : {
                id: page.masterPropertyId?.toString(),
                name: page.masterPropertyName,
              },
        countryId: page.countryId?.toString(),
        area: { id: page.areaId, name: page.areaName, pageStatus: page.areaPageStatus },
        tags: page.tags,
        coords: page.latitude + ',' + page.longitude,
        type: types.EPageType.PROPERTY,
      }
    : {
        countryId: '',
        address: '',
        coords: '',
        tags: [],
        ...initialValues,
        name,
        type: types.EPageType.PROPERTY,
      };

  const isAgent = useSelector(isAgentSelector);
  const isLoginAs = useSelector(isLoginAsSelector);

  const methods = useForm<PropertyFormValues>({
    defaultValues,
    resolver: yupResolver(propertyFormSchema(isAgent)),
    mode: 'onTouched',
  });

  const masterPropRemoved = !isNil(page?.masterPropertyId) && isNil(page?.masterPropertyName);
  /**
   * This code is needed to update attachedPage
   * So far there is no request for such functionality, so the code below is commented out
   */
  const isCreated = false;
  const isReadOnly = isAgent && isCreated && !isLoginAs && !masterPropRemoved;

  return { methods, isReadOnly, isAgent, isCreated };
}

export { usePropertyForm };
